import { wsService } from './websocket.service';
import { useAuthStore } from '@/stores/authStore';

export type LlmChatMessage = { role: string; content: string };

export type LlmChatWsOptions = {
  provider?: string;
  max_tokens?: number;
  temperature?: number;
};

export type LlmChatWsResult = {
  message: { role: string; content: string };
  model: string;
  tokens_used: number;
  latency_ms: number;
};

function normalizeMessage(raw: unknown): { role: string; content: string } {
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const role = typeof o.role === 'string' ? o.role : 'assistant';
    const content = typeof o.content === 'string' ? o.content : '';
    return { role, content };
  }
  return { role: 'assistant', content: '' };
}

/**
 * Request/response LLM chat over the authenticated Go WebSocket (`/api/v1/ws`),
 * matching POST /llm/chat response shape for callers.
 */
export async function requestLlmChatViaWebSocket(
  messages: LlmChatMessage[],
  options?: LlmChatWsOptions
): Promise<LlmChatWsResult> {
  const token = useAuthStore.getState().tokens?.token;
  if (token && typeof localStorage !== 'undefined') {
    localStorage.setItem('authToken', token);
  }

  await wsService.connect();

  const request_id = `llm_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  const timeoutMs = 120_000;

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      wsService.off('llm_chat_response', onResponse);
    };

    const onResponse = (payload: unknown) => {
      if (!payload || typeof payload !== 'object') return;
      const p = payload as Record<string, unknown>;
      if (p.request_id !== request_id) return;

      if (typeof p.error === 'string' && p.error.trim() !== '') {
        cleanup();
        reject(new Error(p.error));
        return;
      }

      cleanup();
      resolve({
        message: normalizeMessage(p.message),
        model: typeof p.model === 'string' ? p.model : String(p.model ?? ''),
        tokens_used: typeof p.tokens_used === 'number' ? p.tokens_used : Number(p.tokens_used ?? 0),
        latency_ms: typeof p.latency_ms === 'number' ? p.latency_ms : Number(p.latency_ms ?? 0),
      });
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('LLM chat WebSocket timed out'));
    }, timeoutMs);

    wsService.on('llm_chat_response', onResponse);

    const data: Record<string, unknown> = {
      request_id,
      messages,
    };
    if (options?.provider !== undefined) data.provider = options.provider;
    if (options?.max_tokens !== undefined) data.max_tokens = options.max_tokens;
    if (options?.temperature !== undefined) data.temperature = options.temperature;

    wsService.sendTypedMessage('llm_chat', data);
  });
}
